import PropTypes from 'prop-types'
import { createContext, useEffect, useReducer } from 'react'
import { API_BASE_URL } from 'src/constants' // utils
import axios from '../utils/axios'
import { isValidToken, setSession } from '../utils/token' // ----------------------------------------------------------------------

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
}

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    }
  },
  LOGIN: (state, action) => {
    const { user } = action.payload

    return {
      ...state,
      isAuthenticated: true,
      user,
    }
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload

    return {
      ...state,
      isAuthenticated: true,
      user,
    }
  },
}

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state)

const AuthContext = createContext({
  ...initialState,
  method: 'token',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
})

AuthProvider.propTypes = {
  children: PropTypes.node,
}

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken')

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken)

          // A dropped connection or a server hiccup is not a rejected login: retry a
          // couple of times before giving up, so a brief outage does not sign users out.
          let response
          for (let attempt = 0; ; attempt += 1) {
            try {
              response = await axios.get(`${API_BASE_URL}/auth/users/me/`)
              break
            } catch (err) {
              const status = err?.response?.status
              const transient = !status || status >= 500
              if (!transient || attempt >= 2) throw err
              await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)))
            }
          }
          const user = response.data

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          })
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          })
        }
      } catch (err) {
        console.error(err)
        // Only a rejected token (401/403) ends the session. On a network or server
        // error the token stays stored, so reloading once the API is back signs in again.
        const status = err?.response?.status
        if (status === 401 || status === 403) setSession(null)
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        })
      }
    }

    initialize()
  }, [])

  const login = async (email, password) => {
    setSession(null)

    const responseLogin = await axios.post(`${API_BASE_URL}/auth/token/login`, {
      username: email,
      password,
    })
    const { auth_token: accessToken } = responseLogin.data

    setSession(accessToken)

    const responseUser = await axios.get(`${API_BASE_URL}/auth/users/me/`)
    const user = responseUser.data

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    })
  }

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post(`${API_BASE_URL}/api/account/register`, {
      email,
      password,
      firstName,
      lastName,
    })
    const { accessToken, user } = response.data

    window.localStorage.setItem('accessToken', accessToken)
    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    })
  }

  const logout = async () => {
    // Revoke the token on the server too; a token left alive after "logout"
    // stayed usable from anywhere it had been copied.
    try {
      await axios.post(`${API_BASE_URL}/auth/token/logout/`)
    } catch (error) {
      // Signing out locally must not depend on the server answering.
    }
    setSession(null)
    dispatch({ type: 'LOGOUT' })
  }

  const resetPassword = () => {}

  const updateProfile = () => {}

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
